import React, { useMemo, useState } from 'react';
import { Modal, Text, Area, Select, Check, Banner, ChecklistRow, EvidenceBadge, Chip } from './ui';
import { useStore, uid } from '../state/store';
import { EVIDENCE_LEVELS } from '../lib/constants';
import {
  ACCOUNT_TIERS,
  ACCOUNT_STATUS_KEYS,
  ACCOUNT_STATUSES,
  SEGMENTS,
  OPP_STAGES,
  OPPORTUNITY_TYPES,
  SCREEN_STATUSES,
  SCORE_CRITERIA,
  candidateScore,
  opportunityReadiness,
  PROMOTION_REASONS,
  DEMOTION_REASONS,
} from '../lib/funnel';
import { orgEvidenceLadder, orgOpportunityStageLabels, orgPrimaryDeal, orgPrimaryDealEnabled, orgSegmentOptions, orgWhyUsLabel } from '../lib/orgPlan';
import { addDays } from '../lib/dates';

function useEvidenceOptions() {
  const { state } = useStore();
  const ladder = orgEvidenceLadder(state);
  if (ladder?.length) {
    return ladder.map((e) => ({ value: e.id, label: `${e.id} — ${e.label || e.meaning}` }));
  }
  return EVIDENCE_LEVELS.map((e) => ({ value: e.id, label: `${e.id} — ${e.name}` }));
}

/* --------------------------------------------------------------- account - */

export function blankAccount(today, overrides = {}) {
  return {
    id: uid('acc'),
    name: '',
    market: '',
    country: '',
    segment: '',
    tier: 'Universe',
    status: 'Universe',
    whyUs: '',
    whyBlocks: '', // legacy alias — prefer whyUs
    relationshipPath: '',
    primaryContact: '',
    decisionMakerKnown: false,
    problemConfirmed: '',
    lastTouch: '',
    nextAction: '',
    nextActionDue: '',
    evidenceLevel: 'E0',
    nexusId: '',
    notes: '',
    pipelineKey: '',
    stageSince: today,
    history: [{ stage: 'Universe', date: today }],
    isNew: true,
    ...overrides,
  };
}

export function AccountModal({ draft, onClose, onSave, onDelete, duplicateNames = [] }) {
  const { state, today } = useStore();
  const [a, setA] = useState(draft);
  const set = (k) => (v) => setA((x) => ({ ...x, [k]: v }));
  const fromNexus = !!(draft.source === 'nexus' || String(draft.nexusId || '').startsWith('acc_'));
  const segmentOptions = orgSegmentOptions(state) || SEGMENTS;
  const whyUsLabel = orgWhyUsLabel(state) || 'Why us / ICP fit';
  const evOptions = useEvidenceOptions();

  const duplicate = useMemo(() => {
    const n = String(a.name || '').trim().toLowerCase();
    return !!n && duplicateNames.some((x) => x.toLowerCase() === n && x.toLowerCase() !== String(draft.name || '').toLowerCase());
  }, [a.name, duplicateNames, draft.name]);

  const save = () => {
    const changedStage = a.status !== draft.status;
    const next = {
      ...a,
      isNew: false,
      stageSince: changedStage ? today : a.stageSince || today,
      history: changedStage ? [...(a.history || []), { stage: a.status, date: today }] : a.history || [],
    };
    onSave(next);
    onClose();
  };

  return (
    <Modal
      title={draft.isNew ? 'Add account' : a.name || 'Account'}
      sub={
        fromNexus
          ? 'Source data from Nexus. Bridge classification is founder-owned.'
          : 'Founder-level record. Manual / CSV is first-class — Nexus is optional.'
      }
      onClose={onClose}
      wide
      footer={
        <>
          {!draft.isNew && <button className="btn danger" onClick={() => { onDelete(a.id); onClose(); }}>Delete</button>}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!String(a.name).trim()} onClick={save}>Save</button>
        </>
      }
    >
      {duplicate && <Banner tone="warn">An account with this name already exists. Add an opportunity to the existing account rather than duplicating it.</Banner>}

      {fromNexus && (
        <div className="grid g2" style={{ marginBottom: 12 }}>
          <div>
            <div className="tiny muted" style={{ marginBottom: 6 }}>Source data: Nexus</div>
            <div className="tiny"><strong>{a.name || '—'}</strong></div>
            <div className="dim tiny">{a.country || '—'} · {a.segment || '—'}{a.primaryContact ? ` · ${a.primaryContact}` : ''}</div>
            <div className="dim tiny">Last touch {a.lastTouch || '—'}{a.crmStatus ? ` · CRM ${a.crmStatus}` : ''}{a.nexusId ? ` · ${a.nexusId}` : ''}</div>
          </div>
          <div>
            <div className="tiny muted" style={{ marginBottom: 6 }}>Bridge classification</div>
            <div className="tiny">{a.tier} · {a.status} · {a.evidenceLevel || 'E0'}</div>
            <div className="dim tiny">{a.classificationWhy || a.whyBlocks || 'No founder classification yet.'}</div>
          </div>
        </div>
      )}

      <div className="grid g3">
        <Text label="Account name" value={a.name} onChange={set('name')} />
        <Text label="Country" value={a.country} onChange={set('country')} />
        <Select label="Segment" value={a.segment} onChange={set('segment')} options={segmentOptions} />
        <Select label="Tier" value={a.tier} onChange={set('tier')} options={ACCOUNT_TIERS} />
        <Select label="Status" value={a.status} onChange={set('status')} options={ACCOUNT_STATUS_KEYS} />
        <Select label="Evidence level" value={a.evidenceLevel} onChange={set('evidenceLevel')} options={evOptions} />
      </div>
      <div className="dim tiny" style={{ marginTop: -4, marginBottom: 10 }}>
        {ACCOUNT_STATUSES.find((s) => s.key === a.status)?.note}
      </div>

      <Area label={whyUsLabel} value={a.whyBlocks} onChange={set('whyBlocks')} rows={2} placeholder="Why would this organisation care? If you cannot answer, it is not a qualified target." />
      <div className="grid g2">
        <Text label="Relationship path" value={a.relationshipPath} onChange={set('relationshipPath')} placeholder="How do we actually get in?" />
        <Text label="Primary contact" value={a.primaryContact} onChange={set('primaryContact')} />
      </div>
      <Check label="Decision-maker known" checked={a.decisionMakerKnown} onChange={set('decisionMakerKnown')} />
      <Area label="Problem / use case confirmed" value={a.problemConfirmed} onChange={set('problemConfirmed')} rows={2} placeholder="What did they say the problem is, in their words?" />

      <div className="grid g3">
        <Text label="Last touch" type="date" value={a.lastTouch} onChange={set('lastTouch')} />
        <Text label="Next action" value={a.nextAction} onChange={set('nextAction')} />
        <Text label="Next action date" type="date" value={a.nextActionDue} onChange={set('nextActionDue')} />
      </div>
      <div className="grid g2">
        <Text label="Nexus reference ID" value={a.nexusId} onChange={set('nexusId')} placeholder="Optional" />
        <Text label="Notes" value={a.notes} onChange={set('notes')} />
      </div>
    </Modal>
  );
}

/* ----------------------------------------------------------- opportunity - */

export function blankOpportunity(today, account, overrides = {}) {
  return {
    id: uid('opp'),
    name: account ? `${account.name} — ` : '',
    accountId: account?.id || '',
    accountName: account?.name || '',
    market: account?.market || '',
    type: '',
    sponsorProblem: account?.problemConfirmed || '',
    whyNow: '',
    commercialObjective: '',
    decisionMaker: account?.primaryContact || '',
    budgetSignal: '',
    timing: '',
    existingAlternative: '',
    evidenceLevel: 'E2',
    stage: 'Qualified',
    qualificationStatus: 'qualified',
    nextAction: '',
    nextActionDue: '',
    assetIdentified: false,
    primaryDealCandidate: false,
    d001Candidate: false, // legacy alias
    primaryDeal: false,
    primaryD001: false, // legacy alias
    nexusId: '',
    notes: '',
    scores: {},
    pipelineKey: account?.pipelineKey || '',
    stageSince: today,
    lastTouch: today,
    history: [{ stage: 'Qualified', date: today }],
    isNew: true,
    ...overrides,
  };
}

export function OpportunityModal({ draft, accounts, onClose, onSave, onDelete }) {
  const { state, today } = useStore();
  const [o, setO] = useState(draft);
  const [tab, setTab] = useState('detail');
  const set = (k) => (v) => setO((x) => ({ ...x, [k]: v }));
  const account = accounts.find((x) => x.id === o.accountId);
  const readiness = opportunityReadiness({ ...account, problemConfirmed: o.sponsorProblem, nextAction: o.nextAction });
  const score = candidateScore(o);
  const evOptions = useEvidenceOptions();
  const stageOptions = orgOpportunityStageLabels(state).length
    ? orgOpportunityStageLabels(state)
    : OPP_STAGES;

  const save = () => {
    const changedStage = o.stage !== draft.stage;
    onSave({
      ...o,
      accountName: account?.name || o.accountName,
      isNew: false,
      stageSince: changedStage ? today : o.stageSince || today,
      history: changedStage ? [...(o.history || []), { stage: o.stage, date: today }] : o.history || [],
    });
    onClose();
  };

  return (
    <Modal
      title={draft.isNew ? 'Create opportunity' : o.name || 'Opportunity'}
      sub="A booked meeting is not an opportunity."
      onClose={onClose}
      wide
      footer={
        <>
          {!draft.isNew && <button className="btn danger" onClick={() => { onDelete(o.id); onClose(); }}>Delete</button>}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!String(o.name).trim() || !o.accountId} onClick={save}>Save</button>
        </>
      }
    >
      <div className="seg" style={{ marginBottom: 14 }}>
        <button className={tab === 'detail' ? 'on' : ''} onClick={() => setTab('detail')}>Opportunity</button>
        <button className={tab === 'score' ? 'on' : ''} onClick={() => setTab('score')}>
          Candidate score {score.scored > 0 ? `${score.total}/50` : ''}
        </button>
      </div>

      {tab === 'detail' ? (
        <>
          {draft.isNew && !readiness.ready && (
            <Banner tone="warn">
              <strong>Recommended before creating an opportunity:</strong> a confirmed problem or use case, a plausible
              sponsor, and a next commercial step. If this is still exploratory, keep it as an account in Discovery.
              <div style={{ marginTop: 6 }}>
                {readiness.checks.map((c) => (
                  <ChecklistRow key={c.key} ok={c.ok} label={c.label} />
                ))}
              </div>
            </Banner>
          )}

          <div className="grid g2">
            <Text label="Opportunity name" value={o.name} onChange={set('name')} />
            <Select
              label="Account"
              value={o.accountId}
              onChange={set('accountId')}
              options={[{ value: '', label: 'Choose an account' }, ...accounts.map((a) => ({ value: a.id, label: a.name || 'Unnamed' }))]}
            />
            <Select label="Opportunity type" value={o.type} onChange={set('type')} options={OPPORTUNITY_TYPES} />
            <Select label="Stage" value={o.stage} onChange={set('stage')} options={stageOptions} />
            <Select label="Evidence level" value={o.evidenceLevel} onChange={set('evidenceLevel')} options={evOptions} />
            <Text label="Decision maker" value={o.decisionMaker} onChange={set('decisionMaker')} />
          </div>

          <Area label="Sponsor problem" value={o.sponsorProblem} onChange={set('sponsorProblem')} rows={2} placeholder="Their words." />
          <div className="grid g2">
            <Text label="Why now" value={o.whyNow} onChange={set('whyNow')} />
            <Text label="Commercial objective" value={o.commercialObjective} onChange={set('commercialObjective')} />
            <Text label="Budget signal" value={o.budgetSignal} onChange={set('budgetSignal')} placeholder="Whose budget, and how do you know?" />
            <Text label="Timing" value={o.timing} onChange={set('timing')} />
            <Text label="Existing alternative" value={o.existingAlternative} onChange={set('existingAlternative')} />
            <Text label="Nexus opportunity ID" value={o.nexusId} onChange={set('nexusId')} placeholder="Optional" />
          </div>

          <div className="grid g2">
            <Text label="Next action" value={o.nextAction} onChange={set('nextAction')} />
            <Text label="Next action date" type="date" value={o.nextActionDue} onChange={set('nextActionDue')} />
          </div>

          <div className="hr" />
          {orgPrimaryDealEnabled(state) && (
            <>
              <Check label="Asset identified" checked={o.assetIdentified} onChange={set('assetIdentified')} />
              <Check
                label={`${orgPrimaryDeal(state)?.label || 'Primary deal'} candidate`}
                checked={o.d001Candidate}
                onChange={set('d001Candidate')}
                help="Requires a real sponsor, a real asset, a commercial objective, a plausible execution path, and enough information to compare."
              />
              {o.primaryD001 && (
                <Chip tone="accent">
                  {orgPrimaryDeal(state)?.label || 'Primary deal'} — change this from the primary-deal workspace
                </Chip>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Banner>
            Score each dimension 0–5. The score structures judgement; it does not make the decision. Do not designate a
            Primary simply because a number is higher.
          </Banner>
          <div className="grid g2" style={{ marginTop: 12 }}>
            {SCORE_CRITERIA.map((c) => (
              <div className="field" key={c.key}>
                <label>{c.label}</label>
                <div className="seg">
                  {[0, 1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={Number(o.scores?.[c.key] || 0) === n ? 'on' : ''}
                      onClick={() => setO((x) => ({ ...x, scores: { ...(x.scores || {}), [c.key]: n } }))}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <span className="help">{c.help}</span>
              </div>
            ))}
          </div>
          <div className="hr" />
          <div className="row">
            <span className="mid-num">{score.total} / 50</span>
            <span className="dim tiny">{score.scored} of 10 dimensions scored{score.complete ? '' : ' — an unscored dimension is missing evidence, not a zero'}</span>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ----------------------------------------------------------------- asset - */

export function blankAsset(opportunity, account, overrides = {}) {
  return {
    id: uid('ast'),
    name: '',
    accountId: account?.id || opportunity?.accountId || '',
    opportunityId: opportunity?.id || '',
    location: '',
    type: '',
    indicativeValue: '',
    incomeProducing: 'Unknown',
    ownershipStatus: '',
    valuationStatus: '',
    documentsAvailable: '',
    sponsorAuthorityConfirmed: false,
    commercialUrgency: '',
    regulatoryComplexity: '',
    capitalFit: '',
    repeatability: '',
    screenStatus: 'Not started',
    notes: '',
    pipelineKey: opportunity?.pipelineKey || account?.pipelineKey || '',
    isNew: true,
    ...overrides,
  };
}

export function AssetModal({ draft, opportunities, onClose, onSave, onDelete }) {
  const [a, setA] = useState(draft);
  const set = (k) => (v) => setA((x) => ({ ...x, [k]: v }));
  return (
    <Modal
      title={draft.isNew ? 'Add asset candidate' : a.name || 'Asset candidate'}
      sub="Create when a real property, project or portfolio is identified."
      onClose={onClose}
      wide
      footer={
        <>
          {!draft.isNew && <button className="btn danger" onClick={() => { onDelete(a.id); onClose(); }}>Delete</button>}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!String(a.name).trim()} onClick={() => { onSave({ ...a, isNew: false }); onClose(); }}>Save</button>
        </>
      }
    >
      <div className="grid g2">
        <Text label="Asset name" value={a.name} onChange={set('name')} />
        <Select
          label="Linked opportunity"
          value={a.opportunityId}
          onChange={(v) => {
            const opp = opportunities.find((o) => o.id === v);
            setA((x) => ({ ...x, opportunityId: v, accountId: opp?.accountId || x.accountId }));
          }}
          options={[{ value: '', label: 'Unlinked' }, ...opportunities.map((o) => ({ value: o.id, label: o.name || 'Unnamed' }))]}
        />
      </div>
      <div className="grid g3">
        <Text label="Location" value={a.location} onChange={set('location')} />
        <Text label="Asset type" value={a.type} onChange={set('type')} />
        <Text label="Indicative value" value={a.indicativeValue} onChange={set('indicativeValue')} />
        <Select label="Income-producing" value={a.incomeProducing} onChange={set('incomeProducing')} options={['Yes', 'No', 'Unknown']} />
        <Text label="Ownership / title status" value={a.ownershipStatus} onChange={set('ownershipStatus')} />
        <Text label="Valuation status" value={a.valuationStatus} onChange={set('valuationStatus')} />
      </div>
      <Text label="Documents available" value={a.documentsAvailable} onChange={set('documentsAvailable')} />
      <div className="grid g3">
        <Text label="Commercial urgency" value={a.commercialUrgency} onChange={set('commercialUrgency')} />
        <Text label="Regulatory complexity" value={a.regulatoryComplexity} onChange={set('regulatoryComplexity')} />
        <Text label="Capital fit" value={a.capitalFit} onChange={set('capitalFit')} />
        <Text label="Repeatability" value={a.repeatability} onChange={set('repeatability')} />
        <Select label="Screen status" value={a.screenStatus} onChange={set('screenStatus')} options={SCREEN_STATUSES} />
      </div>
      <Check label="Sponsor authority confirmed" checked={a.sponsorAuthorityConfirmed} onChange={set('sponsorAuthorityConfirmed')} />
      <Area label="Notes" value={a.notes} onChange={set('notes')} rows={2} />
    </Modal>
  );
}

/* ------------------------------------------------------------ tier change */

export function TierChangeModal({ account, direction, onClose, onSave }) {
  const reasons = direction === 'promote' ? PROMOTION_REASONS : DEMOTION_REASONS;
  const [tier, setTier] = useState(direction === 'promote' ? 'Tier A' : 'Tier B');
  const [reason, setReason] = useState(reasons[0]);
  const [note, setNote] = useState('');

  return (
    <Modal
      title={direction === 'promote' ? `Promote ${account.name}` : `Move ${account.name} down`}
      sub="A tier change without a reason is just a mood."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => { onSave({ tier, reason, note }); onClose(); }}>
            Save change
          </button>
        </>
      }
    >
      <Select label="New tier" value={tier} onChange={setTier} options={ACCOUNT_TIERS} />
      <Select label="Reason" value={reason} onChange={setReason} options={reasons} />
      <Area label="Detail" value={note} onChange={setNote} rows={2} placeholder="What specifically happened?" />
    </Modal>
  );
}

/* ------------------------------------------------- designate / replace --- */

export function DesignatePrimaryModal({ candidates, current, onClose, onSave }) {
  const { today } = useStore();
  const [f, setF] = useState({
    candidateId: candidates[0]?.id || '',
    why: '',
    alternatives: '',
    evidence: '',
    risk: '',
    backupId: '',
    reviewDate: addDays(today, 10),
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const complete = f.candidateId && f.why.trim() && f.alternatives.trim() && f.evidence.trim() && f.risk.trim();

  return (
    <Modal
      title={current ? 'Replace Primary D001' : 'Designate Primary D001'}
      sub="Exactly one opportunity may hold this designation."
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!complete} onClick={() => { onSave(f); onClose(); }}>
            {current ? 'Replace Primary' : 'Designate Primary'}
          </button>
        </>
      }
    >
      <Banner>
        This is normal pipeline management, not a Strategy Exception. The strategy stays locked; only the deal changes.
      </Banner>

      <div style={{ marginTop: 14 }}>
        <Select
          label="Candidate"
          value={f.candidateId}
          onChange={set('candidateId')}
          options={candidates.map((c) => ({ value: c.id, label: `${c.name} — score ${c.score}/50, ${c.evidenceLevel}` }))}
        />
      </div>
      <Area label="Why this candidate?" value={f.why} onChange={set('why')} rows={2} />
      <Area label="What alternatives were considered?" value={f.alternatives} onChange={set('alternatives')} rows={2} placeholder="Name them. 'It arrived first' is not a comparison." />
      <Area label="What evidence supports concentrating here?" value={f.evidence} onChange={set('evidence')} rows={2} />
      <Area label="What is the #1 risk?" value={f.risk} onChange={set('risk')} rows={2} />
      <div className="grid g2">
        <Select
          label="Backup candidate"
          value={f.backupId}
          onChange={set('backupId')}
          options={[{ value: '', label: 'None — concentration risk is high' }, ...candidates.filter((c) => c.id !== f.candidateId).map((c) => ({ value: c.id, label: c.name }))]}
        />
        <Text label="Review date" type="date" value={f.reviewDate} onChange={set('reviewDate')} />
      </div>
      {!f.backupId && <Banner tone="warn">No backup named. Deal concentration will be flagged High until one exists.</Banner>}
    </Modal>
  );
}

export function ReplacePrimaryModal({ current, candidates, onClose, onSave }) {
  const { today } = useStore();
  const [f, setF] = useState({
    candidateId: candidates.find((c) => c.id !== current?.id)?.id || '',
    reason: '',
    evidence: '',
    disposition: 'Parked',
    backupId: '',
    impact: '',
    reviewDate: addDays(today, 10),
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const complete = f.candidateId && f.reason.trim() && f.evidence.trim() && f.impact.trim();

  return (
    <Modal
      title="Replace Primary D001"
      sub={current ? `Current: ${current.name}` : ''}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!complete} onClick={() => { onSave(f); onClose(); }}>Replace</button>
        </>
      }
    >
      <Banner>Primary opportunity changed based on evidence. Strategy remains locked.</Banner>
      <div style={{ marginTop: 14 }}>
        <Select
          label="New Primary D001"
          value={f.candidateId}
          onChange={set('candidateId')}
          options={candidates.filter((c) => c.id !== current?.id).map((c) => ({ value: c.id, label: `${c.name} — score ${c.score}/50` }))}
        />
      </div>
      <Area label="Reason" value={f.reason} onChange={set('reason')} rows={2} placeholder="Sponsor stalled, asset failed, regulatory complexity grew, commitment disappeared, or a superior candidate emerged." />
      <Area label="Evidence" value={f.evidence} onChange={set('evidence')} rows={2} />
      <div className="grid g2">
        <Select label="Old candidate disposition" value={f.disposition} onChange={set('disposition')} options={['Backup', 'Parked', 'Lost', 'Continue as candidate']} />
        <Select
          label="Backup after this change"
          value={f.backupId}
          onChange={set('backupId')}
          options={[{ value: '', label: 'None' }, ...candidates.filter((c) => c.id !== f.candidateId).map((c) => ({ value: c.id, label: c.name }))]}
        />
      </div>
      <Area label="Impact on the 45-day objective" value={f.impact} onChange={set('impact')} rows={2} />
      <Text label="Review date" type="date" value={f.reviewDate} onChange={set('reviewDate')} />
    </Modal>
  );
}
